import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, Empty, Badge, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import { IconPlus } from '../icons.jsx';
import FileViewer from '../FileViewer.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
export const CONTRACT_STATUSES = ['draft', 'sent', 'signed', 'expired', 'cancelled'];
export const CONTRACT_TYPES = ['procacciamento', 'agency', 'nda', 'other'];
export const blankContract = {
  title: '', project_id: '', type: 'procacciamento', status: 'draft',
  value_eur: '', start_date: '', signed_date: '', expiry_date: '', notes: '',
};

/**
 * Create/edit a contract, with versioned file upload. Reused by the Contracts
 * page and the Project detail page. Pass `lockProject` to pin project_id.
 */
export default function ContractForm({ initial, projects = [], isAdmin = false, lockProject = false, onClose, onSaved }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [viewFile, setViewFile] = useState(null);
  const fileInput = useRef(null);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!isNew) api.get(`/contracts/${initial.id}`).then((r) => setFiles(r.files || []));
  }, [initial.id, isNew]);

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/contracts', form);
      else await api.put(`/contracts/${initial.id}`, form);
      toast(isNew ? t('Contract created') : t('Contract saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete contract'), message: t('Delete this contract and its files?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/contracts/${initial.id}`);
    toast(t('Contract deleted'));
    onSaved();
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await api.upload(`/contracts/${initial.id}/files`, fd);
    setFiles((await api.get(`/contracts/${initial.id}`)).files || []);
    if (fileInput.current) fileInput.current.value = '';
  }

  async function markSigned(fileId) {
    if (!(await confirm({
      title: t('Mark as signed'),
      message: t('Mark this version as the signed contract? The contract status will move to Signed.'),
      confirmLabel: t('Mark as signed'),
    }))) return;
    await api.post(`/contracts/${initial.id}/mark-signed/${fileId}`);
    setFiles((await api.get(`/contracts/${initial.id}`)).files || []);
    setForm((f) => ({ ...f, status: 'signed' }));
    toast(t('Contract marked as signed'));
  }

  return (
    <Modal title={isNew ? t('New contract') : form.title} onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify(initial)}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Title')} error={errors.title}><input value={form.title} onChange={set('title')} /></Field>
      <div className="grid-2">
        <Field label={t('Project')} error={errors.project_id}>
          <select value={form.project_id || ''} onChange={set('project_id')} disabled={lockProject}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label={t('Type')}><select value={form.type} onChange={set('type')}>{CONTRACT_TYPES.map((ty) => <option key={ty} value={ty}>{t(ty)}</option>)}</select></Field>
      </div>
      <div className="grid-2">
        <Field label={t('Status')}><select value={form.status} onChange={set('status')}>{CONTRACT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
        <Field label={t('Value (EUR)')}><input type="number" step="0.01" value={form.value_eur || ''} onChange={set('value_eur')} /></Field>
      </div>
      <div className="grid-3">
        <Field label={t('Start')} error={errors.start_date}><input type="date" value={form.start_date || ''} onChange={set('start_date')} /></Field>
        <Field label={t('Signed')} error={errors.signed_date}><input type="date" value={form.signed_date || ''} onChange={set('signed_date')} /></Field>
        <Field label={t('Expiry')} error={errors.expiry_date}><input type="date" value={form.expiry_date || ''} onChange={set('expiry_date')} /></Field>
      </div>
      <Field label={t('Notes')}><textarea rows={2} value={form.notes || ''} onChange={set('notes')} /></Field>

      {!isNew && (
        <div className="files-block">
          <div className="panel-head">
            <h4>{t('Files (versioned)')}</h4>
            <input ref={fileInput} type="file" hidden onChange={uploadFile} />
            <button className="btn ghost sm" type="button" onClick={() => fileInput.current?.click()}>
              <IconPlus width={13} height={13} /> {t('Upload file')}
            </button>
          </div>
          {files.length === 0 ? <Empty>{t('No files uploaded.')}</Empty> : (
            <ul className="line-list">
              {files.map((f) => (
                <li key={f.id}>
                  <div className="ll-main">
                    <span className="ll-title">
                      v{f.version} · {f.original_name}
                      {f.kind === 'signed' && <Badge tone="green" style={{ marginLeft: 8 }}>{t('Signed copy')}</Badge>}
                    </span>
                  </div>
                  <div className="ll-meta">
                    <button type="button" className="btn ghost sm" onClick={() => setViewFile({ path: `/contract-files/${f.id}/download`, name: f.original_name })}>{t('View')}</button>
                    <a className="btn ghost sm" href={`${API_BASE}/contract-files/${f.id}/download`}>{t('Download')}</a>
                    {f.kind !== 'signed' && (
                      <button type="button" className="btn ghost sm" onClick={() => markSigned(f.id)}>{t('Mark as signed')}</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {viewFile && <FileViewer path={viewFile.path} name={viewFile.name} onClose={() => setViewFile(null)} />}
    </Modal>
  );
}
